import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import useMedia from 'common/hooks/useMedia'
import { formatDateTime, formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'
import { useMutateDeleteShrimpTransfer } from 'modules/shrimpTransfer/api/mutation'
import { useGetShrimpTransfer } from 'modules/shrimpTransfer/api/queries'
import { PartialTransfer } from 'modules/shrimpTransfer/api/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, Stack, Typography, Divider, Grid } from '@mui/material'

import { Icon } from '@iconify/react'

import ShrimpTransferRecordDialog from '../ShrimpTransferRecordDialog'
import ShrimpTransferViewDialog from '../ShrimpTransferViewDialog'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'

const ShrimpTransfersRecordBody = () => {
  const { data } = useGetShrimpTransfer()
  const isMobile = useMedia('sm')
  const { t } = useTranslation('shrimpTransfer')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<PartialTransfer | null>(null)
  const [open, setOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string>('')
  const invalidateCostQueries = useInvalidateCostQueries()
  const deleteMutation = useMutateDeleteShrimpTransfer()

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteMutation.mutateAsync(deletingId)
      invalidateCostQueries()
    },
  })

  const handleOpenDeleteConfirm = (id: string) => {
    setDeletingId(id)
    openConfirmDialog()
    handleDialogClose()
  }

  const handleEditOpen = () => {
    setOpen(true)
  }

  const handleEditClose = () => {
    setOpen(false)
  }

  const handleCardClick = (item: PartialTransfer) => {
    setSelectedRecord(item)
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedRecord(null)
  }

  return (
    <Stack p={isMobile ? 0 : 2} pt={isMobile ? 2 : 0}>
      {!data || data.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" height="60vh" spacing={2}>
          <Box>
            <img
              src="/static/components/transfersEmpty.png"
              alt="empty"
              width={220}
              height={160}
              style={{ objectFit: 'contain' }}
            />
          </Box>
          <Typography color="text.secondary" fontSize={16} textAlign="center">
            {t('transfer.emptyTransferLine1')}
            <br />
            {t('transfer.emptyTransferLine2')}
            <br />
            {t('transfer.emptyTransferLine3')}
          </Typography>
        </Stack>
      ) : (
        <Grid container spacing={isMobile ? 1 : 2} width="100%">
          {data?.map((item, idx) => (
            <Grid item xs={12} sm={12} md={6} xl={4} key={idx}>
              <Card
                sx={{
                  p: isMobile ? 1.5 : 2.5,
                  borderRadius: 3,
                  boxShadow: 1,
                  height: '100%',
                  border: '1px solid #E3E8EF',
                  background: '#fff',
                  minWidth: 0,
                }}
                onClick={() => handleCardClick(item)}
                style={{ cursor: 'pointer' }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  {item.type === 'out' ? (
                    <Box
                      sx={{
                        background: '#FFF0F0',
                        borderRadius: '50%',
                        width: isMobile ? 28 : 36,
                        height: isMobile ? 28 : 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon
                        icon="ic:round-arrow-back"
                        color="#F36B6B"
                        width={isMobile ? 20 : 24}
                        height={isMobile ? 20 : 24}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        background: '#E8F5E9',
                        borderRadius: '50%',
                        width: isMobile ? 28 : 36,
                        height: isMobile ? 28 : 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon
                        icon="ic:round-arrow-forward"
                        color="#4CAF50"
                        width={isMobile ? 20 : 24}
                        height={isMobile ? 20 : 24}
                      />
                    </Box>
                  )}
                  <Typography fontWeight="bold" fontSize={isMobile ? 15 : 18}>
                    {item.type === 'out' ? t('transfer.dateOut') : t('transfer.dateIn')}
                  </Typography>
                  <Box flexGrow={1} />
                  <Typography fontWeight="bold" fontSize={isMobile ? 15 : 18}>
                    {formatThaiShortDateTwoDigitYear(item.transfer_date, { showTime: true })}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: isMobile ? 1 : 2 }} />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={2}
                  mb={isMobile ? 1 : 2}
                >
                  <Box
                    sx={{
                      background: '#F8FAFC',
                      borderRadius: 2,
                      px: isMobile ? 1.5 : 2,
                      py: isMobile ? 0.5 : 1,
                      minWidth: isMobile ? 100 : 140,
                      minHeight: isMobile ? 56 : 64,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Typography color="text.secondary" fontSize={isMobile ? 12 : 14}>
                      {t('transfer.from')}
                    </Typography>
                    <Typography
                      color="primary.main"
                      fontWeight="bold"
                      fontSize={isMobile ? 16 : 18}
                    >
                      {item.source_pond_name}
                    </Typography>
                  </Box>
                  <Icon
                    icon="mdi:arrow-right-bold"
                    color="#4CAF50"
                    width={isMobile ? 24 : 32}
                    height={isMobile ? 24 : 32}
                  />
                  <Box
                    sx={{
                      background: '#F8FAFC',
                      borderRadius: 2,
                      px: isMobile ? 1.5 : 2,
                      py: isMobile ? 0.5 : 1,
                      minWidth: isMobile ? 100 : 140,
                      minHeight: isMobile ? 56 : 64,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Typography color="text.secondary" fontSize={isMobile ? 12 : 14}>
                      {t('transfer.toPond')}
                    </Typography>
                    <Typography
                      color="primary.main"
                      fontWeight="bold"
                      fontSize={isMobile ? 16 : 18}
                    >
                      {item.destination_pond_name}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography color="text.secondary" fontSize={isMobile ? 13 : 16}>
                    {t('transfer.shrimpSize', { species })}
                  </Typography>
                  <Typography fontWeight="bold" fontSize={isMobile ? 13 : 16}>
                    {fNumber(item.amount_per_kg)}{' '}
                    <span style={{ color: '#B0BEC5', fontWeight: 400 }}>
                      ({t(species === SpeciesName.Fish ? 'transfer.gram' : 'transfer.amountPerKg')})
                    </span>
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography color="text.secondary" fontSize={isMobile ? 13 : 16}>
                    {t('transfer.quantity')}
                  </Typography>
                  <Typography fontWeight="bold" fontSize={isMobile ? 13 : 16}>
                    {fNumber(item.amount)}{' '}
                    <span style={{ color: '#B0BEC5', fontWeight: 400 }}>{t('transfer.kg')}</span>
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary" fontSize={isMobile ? 13 : 16}>
                    {isFish ? t('transfer.cost.per.one') : t('transfer.cost.per.kg')}
                  </Typography>
                  <Typography fontWeight="bold" fontSize={isMobile ? 13 : 16}>
                    {fNumber(item.cost_per_kg)}{' '}
                    <span style={{ color: '#B0BEC5', fontWeight: 400 }}>{t('transfer.baht')}</span>
                  </Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <ShrimpTransferViewDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        data={selectedRecord as PartialTransfer}
        onEdit={handleEditOpen}
        onDelete={handleOpenDeleteConfirm}
      />
      <ShrimpTransferRecordDialog
        open={open}
        onClose={handleEditClose}
        onViewDialogClose={handleDialogClose}
        record={selectedRecord as PartialTransfer}
        mode="edit"
      />
      {dialog}
    </Stack>
  )
}

export default ShrimpTransfersRecordBody
